import {
  Alert,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  FormHelperText,
} from '@munifocus/ui';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { LocalGovernmentProfile } from '@munifocus/contracts-gov';
import { useLocalGovernments } from '../../lib/local-governments-api';
import type { FileForm } from './useFileForm';
import type { Focusable } from '../../lib/useSubmitValidation';

/** The letterhead name in the officer's language, falling back to the common one. */
export function localGovernmentLabel(lg: LocalGovernmentProfile, language: string): string {
  const official = language?.startsWith('fr') ? lg.officialNameFr : lg.officialNameEn;
  return official?.trim() || lg.name;
}

/**
 * Which local government's enforcement this file is.
 *
 * This is the anchor the document assembler reads letterhead from. Without it a
 * generated Form 4 fills its "local government giving notice" field from
 * `taxingAuthority` — an assessment-roll string off an SNB record, not the
 * registry's official bilingual name — so an unanchored file is a file whose
 * statutory notice prints the wrong name on it.
 *
 * `unanchored` is offered rather than left as an empty default because it is a
 * real answer: local service districts and unincorporated areas have no registry
 * row and cannot be anchored to one. Picking it says so; leaving the field alone
 * only says the officer hasn't got to it yet.
 */
export function LocalGovernmentPicker({
  form,
  autoSelectSole = false,
  error,
  inputRef,
  disabled = false,
}: {
  form: FileForm;
  /**
   * Pre-select when the officer holds exactly one appointment.
   *
   * On for intake, where any answer is better arrived at than left blank and an
   * officer appointed at one municipality has only one right answer. Off when
   * editing: an existing file is either already anchored or deliberately not,
   * and silently anchoring it on open would dirty the form with a decision the
   * officer never made.
   */
  autoSelectSole?: boolean;
  /** Set by the form after a submit with no local government chosen. */
  error?: string;
  inputRef?: (el: Focusable | null) => void;
  disabled?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const registry = useLocalGovernments();
  const { values, set } = form;
  const options = registry.data ?? [];

  const sole = options.length === 1 ? options[0].id : null;
  // Latched, so this is a default and not a correction: an officer who picks
  // "none" on a form where they hold one appointment must not have it filled
  // back in under them the next time the component renders.
  const applied = useRef(false);
  useEffect(() => {
    if (applied.current || !autoSelectSole || !sole) return;
    if (values.localGovernmentId !== '') return;
    applied.current = true;
    set('localGovernmentId', sole);
  }, [autoSelectSole, sole, values.localGovernmentId, set]);

  return (
    <Stack spacing={1}>
      <FormControl
        fullWidth
        size="small"
        disabled={disabled || registry.isLoading}
        error={Boolean(error)}
      >
        <InputLabel>{t('gov.file.localGovernment')}</InputLabel>
        <Select
          label={t('gov.file.localGovernment')}
          aria-label={t('gov.file.localGovernment')}
          inputRef={inputRef}
          value={values.localGovernmentId}
          onChange={(e) => set('localGovernmentId', e.target.value)}
        >
          {/* Not a placeholder — the answer for an LSD or unincorporated area,
              which has no registry row to point at. */}
          <MenuItem value="">{t('gov.file.localGovernmentNone')}</MenuItem>
          {options.map((lg) => (
            <MenuItem key={lg.id} value={lg.id}>
              {localGovernmentLabel(lg, i18n.language)}
            </MenuItem>
          ))}
        </Select>
        {error && <FormHelperText>{error}</FormHelperText>}
      </FormControl>

      {registry.isError && <Alert severity="error">{t('gov.file.localGovernmentLoadError')}</Alert>}

      {/* Says what the file loses by staying unanchored, at the moment the
          officer is choosing to leave it that way. */}
      {!registry.isLoading && !registry.isError && values.localGovernmentId === '' && (
        <Alert severity="info" sx={{ py: 0 }}>
          {options.length === 0
            ? t('gov.file.localGovernmentNoAppointments')
            : t('gov.file.localGovernmentUnanchoredHint')}
        </Alert>
      )}
    </Stack>
  );
}
